import React, { useContext, useEffect, useRef, useState } from 'react';
import SEO from '@components/Common/SEO/index';
import FooterV2 from '@components/FooterV2';
import Navigation from '@components/Navigation';
import {
  GlobalDispatchContext,
  GlobalStateContext,
} from '@src/context/GlobalContextProvider';
import CrioSATMockInterview from '@components/Initiatives/CrioSATMockInterview';
import HeroInfo from '@components/Initiatives/HeroInfo';
import LeadGenerationOpenForm from '@components/LeadGenerationForm/LeadGenerationOpenForm';
import ReportSummarySection from '@components/Initiatives/ReportSummarySection';
import WhyTakeThisSection from '@components/Initiatives/WhyTakeThisSection';
import HowItWorks from '@components/Initiatives/HowItWorks';
import RealFeedback from '@components/Initiatives/RealFeedback';
import FAQSection from '@components/v5/FAQSection/index';
// import MockInterviewCTA from '@components/Initiatives/MockInterviewCTA';
import PlacementOutcomes from '@components/Initiatives/PlacementOutcomes';
import AboutSection from '@components/Initiatives/AboutSection';
import BottomStrip from '@components/PPCNavbar/BottomStrip';
import { ELeadSquaredActivityCode } from '@src/constants/leadsquaredActivityConstants';
import LeadGenerationService from '../../../../utils/lead-generation';
import { getQueryParams } from '@src/utils/helper';
import { useLocation } from '@reach/router';

const steps = [
  {
    step: 'Step:01',
    title: 'Select domain',
    description: 'Select domain (FullStack, Data Analytics)',
    icon: '/images/Icon1.webp',
  },
  {
    step: 'Step:02',
    title: 'Take the mock interview',
    description: 'Take the 30-min online mock interview',
    icon: '/images/Icon2.webp',
  },
  {
    step: 'Step:03',
    title: 'Receive your report',
    description: 'Receive your AI-analyzed report instantly',
    icon: '/images/Icon3.webp',
  },
  {
    step: 'Step:04',
    title: 'Use the insights',
    description:
      'Use the insights to fix gaps or enroll in our targeted training program',
    icon: '/images/Icon4.webp',
  },
];

const faqs = [
  {
    question: 'Is the Crio-SAT mock interview free?',
    answer: 'It’s available for a minimal amount of just Rs. 99',
  },
  {
    question: 'How do I get the assessment report?',
    answer:
      'After completing the 30-minute mock interview, you can request your personalized assessment report through the CTA button.',
  },
  {
    question: 'What happens after the test?',
    answer:
      "After the test, you'll receive a detailed AI-generated assessment report and have the option to book a 1:1 session with an expert for further guidance.",
  },
  {
    question: 'How long does the mock interview take?',
    answer: 'The mock interview takes 30 minutes to complete.',
  },
  {
    question: 'Can I take the Crio-SAT mock interview multiple times?',
    answer: 'Yes, you can request our Program Advisor for the same.',
  },
  {
    question: 'What skills are assessed in the Crio-SAT mock interview?',
    answer:
      'The mock interview evaluates key skills in 2 key domains: MERN (Full-stack) and Data Analytics.',
  },
  {
    question: 'Will I get placement after completing the Crio-SAT?',
    answer:
      'No, completing the Crio-SAT does not guarantee placement. However, it offers a simulated experience of a real tech interview, helping you gain valuable insights and better prepare for actual job interviews.',
  },
];

export default function SkillQAssessmentPage(props) {
  const leadFormRef = useRef();
  const globalState = useContext(GlobalStateContext);
  const globalDispatch = useContext(GlobalDispatchContext);
  const location = useLocation();

  const [savedInterviewData, setSavedInterviewData] = useState(null);
  const [hasMounted, setHasMounted] = useState(false);

  const queryParams = getQueryParams(location.search);

  const satPaymentActivityHandler = async () => {
    try {
      const leadAndActivityPayload = {
        LeadDetails: [
          {
            Attribute: 'EmailAddress',
            Value: globalState.email.toLowerCase(),
          },
        ],
        Activity: {
          ActivityEvent: ELeadSquaredActivityCode.PAYMENT_CAPTURE,
          Fields: [
            {
              SchemaName: 'mx_Custom_1',
              Value: 99,
            },
            {
              SchemaName: 'mx_Custom_3',
              Value: queryParams.payment_id,
            },
          ],
        },
      };

      await LeadGenerationService.queueLeadAndActivity(leadAndActivityPayload);
    } catch (error) {
      console.error('debug > ', error);
    }
  };

  useEffect(() => {
    setHasMounted(true);
    const interviewData = globalState?.interviewData;

    if (!globalState.satPaymentId) {
      globalDispatch({
        type: 'SET_SAT_PAYMENT_ID',
        payload: queryParams.payment_id,
      });
    }

    if (interviewData?.activation_time) {
      const activationTime = new Date(interviewData.activation_time);
      const expirationTime = new Date(
        activationTime.getTime() + 2 * 60 * 60 * 1000,
      ); // +2 hours
      const currentTime = new Date();

      if (currentTime > expirationTime) {
        globalDispatch({
          type: 'SET_INTERVIEW_RESPONSE',
          payload: null,
        });
        setSavedInterviewData(null);
      } else {
        setSavedInterviewData(interviewData);
      }
    } else {
      setSavedInterviewData(interviewData);
    }
  }, [globalState?.interviewData]);

  if (!hasMounted) return null; // ✅ Prevent flicker and mismatch

  return (
    <div>
      <div className="mx-auto w-full md:max-w-[1200px]">
        <SEO
          canonicalUrl={'https://crio.do/placements'}
          title={'Crio Skill Assessment'}
          description={'Book your mock interview slot with Crio'}
        />
        <div className="relative mx-4 h-fit max-w-[1300px] pb-20 md:mx-auto md:px-4">
          <div className="fixed left-0 top-0 z-[1001] w-screen">
            <Navigation hideTopStrip hideCTA />
          </div>

          {savedInterviewData ? (
            <CrioSATMockInterview interviewData={savedInterviewData} />
          ) : (
            <>
              <div className="mx-4 mt-[80px] flex h-full flex-col gap-10 md:h-[600px] md:flex-row">
                <HeroInfo />
                <div ref={leadFormRef}>
                  <LeadGenerationOpenForm
                    isOpenForm={true}
                    hasProgramInterestedField
                    wrapperClassName={'flex justify-center [&>div]:h-[545px]'}
                    pageOneButtonText={'AI Mock Interview @ ₹99'}
                    pageTwoButtonText={'AI Mock Interview @ ₹99'}
                  />
                </div>
              </div>
              <ReportSummarySection />
              <WhyTakeThisSection />
              <HowItWorks steps={steps} />
              <AboutSection />
              <PlacementOutcomes />
              <RealFeedback />
              <FAQSection data={faqs} postContent={null} />
              {/* <MockInterviewCTA
                onClick={() =>
                  leadFormRef.current?.scrollIntoView({ behavior: 'smooth' })
                }
              /> */}
            </>
          )}
        </div>
      </div>
      <FooterV2 />
      <BottomStrip />
    </div>
  );
}
